import BaseStore from '@/store/baseStore'
import api from '@/api/api'
import API from '@/api/apiUrls'
import {
  createLinkElementThenDownloadFile,
  getDownloadFileNameFromHeaders
} from '@/utils/downloadFile'

export const namespaced = true

const baseURI = API.SCHEMATISATION.STAB_SCHEMATISATION.INDEX

const baseStore = new BaseStore(baseURI)

const getDefaultState = () => {
  return {
    ...structuredClone(baseStore.state),
    status: 'empty',
    model: {},
    currentOverviewItem: null,
    showList: false,
    duplicateDialog: false,
    duplicateLoading: false,
    exportFactSheetsDialog: false,
    exportFactSheetsLoading: false,
    exportDStabilityDialog: false,
    exportDStabilityLoading: false,
    exportJSONDialog: false,
    exportJSONLoading: false
  }
}
export const state = getDefaultState()

export const mutations = {
  ...baseStore.mutations,
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
  TOGGLE_SHOW_LIST(state, bool) {
    state.showList = bool
  },
  TOGGLE_DUPLICATE_DIALOG(state) {
    state.duplicateDialog = !state.duplicateDialog
  },
  TOGGLE_DUPLICATE_LOADING(state) {
    state.duplicateLoading = !state.duplicateLoading
  },
  TOGGLE_EXPORT_FACT_SHEETS_DIALOG(state) {
    state.exportFactSheetsDialog = !state.exportFactSheetsDialog
  },
  TOGGLE_EXPORT_FACT_SHEETS_LOADING(state) {
    state.exportFactSheetsLoading = !state.exportFactSheetsLoading
  },
  TOGGLE_EXPORT_DSTABILITY_DIALOG(state) {
    state.exportDStabilityDialog = !state.exportDStabilityDialog
  },
  TOGGLE_EXPORT_DSTABILITY_LOADING(state) {
    state.exportDStabilityLoading = !state.exportDStabilityLoading
  },
  TOGGLE_EXPORT_JSON_DIALOG(state) {
    state.exportJSONDialog = !state.exportJSONDialog
  },
  TOGGLE_EXPORT_JSON_LOADING(state) {
    state.exportJSONLoading = !state.exportJSONLoading
  },
  SET_CURRENT_OVERVIEW_ITEM(state, item) {
    state.currentOverviewItem = item
  }
}
export const actions = {
  ...baseStore.actions,
  resetState(context) {
    context.commit('resetState')
  },
  setCurrentOverviewItem({ commit }, payload) {
    commit('SET_CURRENT_OVERVIEW_ITEM', payload)
  },
  async toggleShowList({ commit }, bool) {
    commit('TOGGLE_SHOW_LIST', bool)
  },
  toggleDuplicateDialog({ commit }) {
    commit('TOGGLE_DUPLICATE_DIALOG')
  },
  toggleExportFactSheetsDialog({ commit }) {
    commit('TOGGLE_EXPORT_FACT_SHEETS_DIALOG')
  },
  toggleExportFactSheetsLoading({ commit }) {
    commit('TOGGLE_EXPORT_FACT_SHEETS_LOADING')
  },
  toggleExportDStabilityDialog({ commit }) {
    commit('TOGGLE_EXPORT_DSTABILITY_DIALOG')
  },
  toggleExportDStabilityLoading({ commit }) {
    commit('TOGGLE_EXPORT_DSTABILITY_LOADING')
  },
  toggleExportJsonDialog({ commit }) {
    commit('TOGGLE_EXPORT_JSON_DIALOG')
  },
  async duplicate({ commit }, payload) {
    try {
      commit('TOGGLE_DUPLICATE_LOADING')
      const url = `${baseURI}duplicate/`
      const response = await api.post(url, payload)
      commit('ADD_ITEM', response.data)
    } finally {
      commit('TOGGLE_DUPLICATE_LOADING')
    }
  },
  async resetChildrenState({ dispatch }) {
    const storesToReset = [
      'statePoints',
      'topoProfilePoint',
      'soilLayer',
      'phreaticLine',
      'phreaticReferenceLine',
      'headLine',
      'headReferenceLine',
      'uniformLoads',
      'treeLoads'
    ]
    for (const store of storesToReset) {
      await dispatch(`${store}/resetState`, null, { root: true })
    }
  },
  async exportFactSheets({ commit }, schematisationId) {
    try {
      commit('TOGGLE_EXPORT_FACT_SHEETS_LOADING')
      const url = `${baseURI}${schematisationId}/export_fact_sheets/`
      const { data, headers } = await api.get(url, { responseType: 'blob' })
      createLinkElementThenDownloadFile(
        data,
        getDownloadFileNameFromHeaders(headers) || 'fact-sheets.xlsx'
      )
    } finally {
      commit('TOGGLE_EXPORT_FACT_SHEETS_LOADING')
    }
  },
  async exportDStability({ commit }, schematisationId) {
    try {
      commit('TOGGLE_EXPORT_DSTABILITY_LOADING')
      const url = `${baseURI}${schematisationId}/export_dstability/`
      const { data, headers } = await api.get(url, { responseType: 'blob' })
      createLinkElementThenDownloadFile(
        data,
        getDownloadFileNameFromHeaders(headers) || 'dstability.stix'
      )
    } finally {
      commit('TOGGLE_EXPORT_DSTABILITY_LOADING')
    }
  },
  async exportJSON({ commit }, { schematisationId, revisionId }) {
    try {
      commit('TOGGLE_EXPORT_JSON_LOADING')
      const url = `${baseURI}${schematisationId}/get_json_for_post/`
      const revisionData = {
        bursting_parameter_revision_id: revisionId
      }
      const { data, headers } = await api.post(url, revisionData, { responseType: 'blob' })
      createLinkElementThenDownloadFile(
        data,
        getDownloadFileNameFromHeaders(headers) || 'post.json'
      )
    } finally {
      commit('TOGGLE_EXPORT_JSON_LOADING')
    }
  }
}

export const getters = {
  isLinked: (state) => (stabSchematisationStage) => {
    if (!state.currentItem?.id) return false
    return stabSchematisationStage.linked_stab_schematisation?.includes(state.currentItem.id)
  },
  isLockedCurrent(state, getters, rootState) {
    const currentSchematisationStage = rootState.stabSchematisationStage.currentItem
    if (state.currentItem && currentSchematisationStage) {
      return getters.isLinked(currentSchematisationStage) || currentSchematisationStage.done
    }
    return false
  },
  isDeleteSelf: (state) => (route) => {
    const currentOverviewId = state.currentOverviewItem ? state.currentOverviewItem.id : null
    return (
      route.name === 'schematisation-stage' &&
      parseInt(route.params.schematisationId) === currentOverviewId
    )
  }
}
